import { useEffect, useRef, useState } from 'react';
import { uiText } from '../../i18n';
import { endpoints } from '../../api';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { StudioOrderTail } from '../studio/StudioOrderTail';
import { studioFrameSize } from '../studio/studio-order-tail-model';
import { record, rows, text } from '../studio/studio-content';
import { studioMediaUrl } from '../studio/studio-preview-model';
import { publishStudioOrder, type StudioOrderSnapshot } from '../studio/studio-job-bridge';
import { stageVideoResult } from './video-result-entry';
import { videoPolicy } from './video-model';
import { videoActionId, videoResultTarget } from './video-result-target';
import { useStudioOrderSourcePreview } from '../studio/use-studio-photo-previews';
import '../photo/server-photo.css';
const videoFormatWords: Record<string, string> = { '1:1': 'Квадрат', '3:4': 'Портрет', '4:5': 'Пост', '9:16': 'Сторис', '16:9': 'Горизонтальное' };
export function VideoOrderResult({
  state,
  resultActions,
  kind = 'video',
  allowAddSpeech = true,
  requireConfirmedAudio = false,
}: {
  state: CircleFormState;
  resultActions?: unknown;
  kind?: StudioOrderSnapshot['kind'];
  allowAddSpeech?: boolean;
  requireConfirmedAudio?: boolean;
}) {
  const last = useRef<StudioOrderSnapshot | null>(null);
  const [error, setError] = useState('');
  const [transferring, setTransferring] = useState(false);
  const transferRequest = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      transferRequest.current?.abort();
      transferRequest.current = null;
    },
    [],
  );
  const owner = state.intent?.owner,
    appId = state.form?.app_id ?? state.intent?.body.app_id,
    id = state.orderId,
    status = state.order?.status;
  useEffect(() => {
    if (owner && appId && id && status) {
      last.current = { owner, appId, orderId: id, status, kind, shown: true };
      publishStudioOrder(last.current);
    }
  }, [owner, appId, id, status, kind]);
  useEffect(() => {
    const clear = (event: Event) => {
      if ((event as CustomEvent).detail?.id === last.current?.orderId) last.current = null;
    };
    document.addEventListener('steos:studio:order-clear', clear);
    return () => {
      document.removeEventListener('steos:studio:order-clear', clear);
      if (last.current) publishStudioOrder({ ...last.current, shown: false });
    };
  }, []);
  /* Ролик итога — сразу в рамке (§16: результат — ролик; «Скачать»): читаем файл один раз, как только сервер его отдал,
     как MotionTail. Без этого итог показывал только ссылку «Открыть результат 1» и не давал «Скачать». */
  const opened = useRef('');
  const order = state.order;
  const mp4 = order?.result?.available ? order.result.assets.find(asset => asset.mime === 'video/mp4') : undefined;
  useEffect(() => {
    if (status !== 'done' || !mp4 || state.result || state.busy) return;
    const key = `${id}:${order?.result_revision}:${mp4.index}`;
    if (opened.current === key) return;
    opened.current = key;
    void state.openResult(mp4.index);
  }, [status, mp4, id, order?.result_revision, state]);
  /* Герой «Оживить фото», «Объёмного фото», «Видео товара»: снимок входа размыт в рамке, пока сервер не прислал display.picture */
  const source = useStudioOrderSourcePreview(state, 'photo', !!id && status !== 'done');
  if (!id) return null;
  const transfer = async (speech: 'original' | 'text') => {
    if (transferRequest.current) return;
    const request = new AbortController();
    transferRequest.current = request;
    setTransferring(true);
    const timer = setTimeout(() => request.abort(), 30000);
    try {
      setError('');
      const me = await endpoints.me();
      if (request.signal.aborted) return;
      if (me.user.id !== owner) throw new Error('owner_changed');
      const target = await videoResultTarget(speech, request.signal);
      const order = await endpoints.speech.get(id, request.signal);
      const asset = order.result?.assets.find(a => a.mime === 'video/mp4');
      if (order.status !== 'done' || !order.result?.available || !asset || order.result_revision !== state.order?.result_revision)
        throw new Error('source_changed');
      // Новое чтение подтверждает доступ владельца; внешние URL результата не используются.
      const blob = await endpoints.speech.result(order, asset.index, request.signal);
      if (request.signal.aborted) return;
      if (blob.type !== 'video/mp4') throw new Error('invalid_result');
      stageVideoResult({
        owner: me.user.id,
        orderId: id,
        revision: order.result_revision,
        index: asset.index,
        file: new File([blob], 'video.mp4', { type: blob.type }),
        speech,
        appId: target.appId,
        format: target.format,
      });
    } catch {
      if (transferRequest.current === request) setError('Не удалось передать видео. Откройте исходный заказ и попробуйте снова.');
    } finally {
      clearTimeout(timer);
      if (transferRequest.current === request) {
        transferRequest.current = null;
        setTransferring(false);
      }
    }
  };
  const inputs = (state.intent?.body.inputs ?? state.inputs ?? {}) as Record<string, unknown>;
  const display = record(record(state.order).display);
  const policy = state.form ? videoPolicy(state.form) : null;
  const styleId = typeof inputs.style === 'string' ? inputs.style : '';
  const styleName =
    text(record(display.style).name) ||
    text(display.style_name) ||
    text(rows(policy?.styles).find(style => String(style.id) === styleId)?.name);
  const card = [
    typeof inputs.reference === 'string' && inputs.reference
      ? 'Свой референс'
      : styleId === 'none'
        ? 'Без стиля'
        : styleName
          ? `Стиль «${styleName}»`
          : '',
    text(display.format) ||
      (inputs.format === 'src' || inputs.format === 'auto' ? (inputs.video ? 'Как у видео' : 'Как у фото') : (videoFormatWords[String(inputs.format)] ?? '')),
    typeof inputs.duration === 'string' && inputs.duration ? `${inputs.duration} с` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const picture = studioMediaUrl(record(display.picture).url) || source;
  const ratio = typeof inputs.format === 'string' && /^\d+:\d+$/.test(inputs.format) ? inputs.format.replace(':', '/') : '9/16';
  const circleBlocked =
    state.inputs.audio === 'false' ||
    !videoActionId(resultActions, 'original') ||
    (requireConfirmedAudio && !state.order?.result?.assets.some(a => a.mime === 'video/mp4' && a.has_audio === true)) ||
    (!!state.form && videoPolicy(state.form)?.mode === 'parallax');
  return (
    <section className="video-order" aria-live="polite">
      <StudioOrderTail
        state={state}
        texts={{
          run: 'Создаём видео',
          runSub: 'Можно уйти с экрана: бот пришлёт ролик в чат.',
          done: 'Видео готово',
          doneSub: 'Бот уже прислал его в чат, в отдельную тему.',
          donePending: 'Бот пришлёт его в чат, в отдельную тему.',
          failed: 'Видео не создано',
          refusal: inputs.video ? 'Заменить видео' : inputs.photo ? 'Заменить фото' : 'Изменить описание',
          watermark: 'на видео',
        }}
        hero={
          <div className="pr-f studio-order-frame" style={{ aspectRatio: ratio, ...studioFrameSize(ratio) }}>
            <span className="pr-pic">{picture && <i><img src={picture} alt="" /></i>}</span>
          </div>
        }
        card={card}
        actions={[
          ...(state.result?.mime === 'video/mp4' ? [{ label: 'Скачать', href: state.result.url, download: state.result.name }] : []),
          {
            label: 'Ещё вариант',
            onClick: async () => {
              if (await state.returnToForm()) document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id } }));
            },
          },
        ]}
        next={
          state.order?.result?.available
            ? [
                ...(!circleBlocked ? [{ label: 'Сделать кружок', disabled: transferring, onClick: () => void transfer('original') }] : []),
                ...(allowAddSpeech && videoActionId(resultActions, 'text')
                  ? [{ label: 'Добавить речь', disabled: transferring, onClick: () => void transfer('text') }]
                  : []),
              ]
            : []
        }
      >
        {status === 'uncertain' && (
          <button type="button" className="ar-lnk" disabled={state.busy || state.submissionBlocked} onClick={() => void state.refreshStatus()}>
            {uiText('Проверить статус заказа')}
          </button>
        )}
        {state.order?.result?.available &&
          state.result?.mime !== 'video/mp4' &&
          !state.busy &&
          state.order.result.assets.map(asset => (
            <button type="button" className="ar-lnk" key={asset.index} disabled={state.busy} onClick={() => void state.openResult(asset.index)}>
              {uiText('Открыть результат')} {asset.index + 1}
            </button>
          ))}
        {status === 'done' && mp4 && !state.result && state.busy && <p role="status">{uiText('Загружаем видео…')}</p>}
        {status === 'done' && !state.order?.result?.available && <p>{uiText('Результат недоступен. Проверьте историю заказа.')}</p>}
        {state.result?.mime === 'video/mp4' && (
          <div className="video-result">
            <video controls playsInline src={state.result.url} />
          </div>
        )}
        {status === 'done' &&
          requireConfirmedAudio &&
          state.order?.result?.available &&
          !state.order.result.assets.some(a => a.mime === 'video/mp4' && a.has_audio === true) && (
            <p>{uiText('Преобразование в кружок недоступно: в результате нет подтверждённой звуковой дорожки.')}</p>
          )}
        {error && <p role="alert">{uiText(error)}</p>}
      </StudioOrderTail>
    </section>
  );
}
